import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import type { PnlCalculationResult } from '../../hooks/usePnlCalculation';
import { formatVolume } from '../../utils/formatters';

interface BalanceBreakdownTooltipProps {
  pnl: PnlCalculationResult;
  theme: any;
  formatUsdt: (val: number | null | undefined) => string;
  symbol: string;
}

export const BalanceBreakdownTooltip: React.FC<BalanceBreakdownTooltipProps> = ({
  pnl,
  theme,
  formatUsdt,
  symbol
}) => {
  // Safe number helper
  const safeNum = (v: number | null | undefined): number => v != null && !isNaN(v) ? v : 0;

  // Spot Cost Basis = Avg Price (USDT) * Remaining Volume
  const spotCostBasis = (pnl.spot.avgPriceUsdt ?? 0) * safeNum(pnl.balance.spotRemaining);

  // Spot Cost Basis (Original Quote) for reference
  const spotCostBasisQuote = (pnl.spot.avgPriceQuote ?? 0) * safeNum(pnl.balance.spotRemaining);
  const quoteCurrency = pnl.spot.quoteCurrency || 'KRW';
  const isNonUsdt = pnl.spot.isNonUsdtQuote;

  // Futures Entry Value (Notional) of Remaining Position
  const futuresEntryNotional = (pnl.futures.avgPrice ?? 0) * Math.abs(pnl.balance.futuresRemaining);

  // Current Values
  const spotCurrentValue = (pnl.spot.currentPriceUsdt ?? 0) * safeNum(pnl.balance.spotRemaining);
  const futuresCurrentValue = (pnl.futures.currentPrice ?? 0) * Math.abs(pnl.balance.futuresRemaining);

  return (
    <Box sx={{ p: 1.5, minWidth: 280 }}>
      {/* Header */}
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'common.white' }}>
        Balance Breakdown
      </Typography>
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />

      {/* Spot Breakdown */}
      <Box sx={{ mb: 1.5 }}>
        {/* Spot Total (Original) */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.spot, display: 'block', mb: 0.5 }}>
          Spot Purchased ({formatVolume(pnl.balance.spotTotal, 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Cost Basis:</Typography>
            <Box sx={{ textAlign: 'right' }}>
              {isNonUsdt && (
                <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem', display: 'block', lineHeight: 1 }}>
                  {quoteCurrency === 'KRW' ? '₩' : quoteCurrency === 'BTC' ? '₿' : ''}
                  {((pnl.spot.avgPriceQuote ?? 0) * safeNum(pnl.balance.spotTotal)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: quoteCurrency === 'KRW' ? 0 : 6 })}
                </Typography>
              )}
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {formatUsdt((pnl.spot.avgPriceUsdt ?? 0) * safeNum(pnl.balance.spotTotal))}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Spot Remaining */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.spot, display: 'block', mb: 0.5 }}>
          Spot Remaining ({formatVolume(pnl.balance.spotRemaining, 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Cost Basis:</Typography>
            <Box sx={{ textAlign: 'right' }}>
              {isNonUsdt && (
                <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem', display: 'block', lineHeight: 1 }}>
                  {quoteCurrency === 'KRW' ? '₩' : quoteCurrency === 'BTC' ? '₿' : ''}
                  {spotCostBasisQuote.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: quoteCurrency === 'KRW' ? 0 : 6 })}
                </Typography>
              )}
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(spotCostBasis)}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Current Value:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {formatUsdt(spotCurrentValue)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Futures Breakdown */}
      <Box>
        {/* Futures Total (Original) */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.futures, display: 'block', mb: 0.5 }}>
          Futures Shorted ({formatVolume(pnl.balance.futuresTotal, 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Entry Value:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {formatUsdt((pnl.futures.avgPrice ?? 0) * safeNum(pnl.balance.futuresTotal))}
            </Typography>
          </Box>
        </Box>

        {/* Futures Remaining */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.price.futures, display: 'block', mb: 0.5 }}>
          Futures Remaining ({formatVolume(Math.abs(pnl.balance.futuresRemaining), 4)} {symbol})
        </Typography>
        <Box sx={{ pl: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Entry Value:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(futuresEntryNotional)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Current Value:</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{formatUsdt(futuresCurrentValue)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
